/**
 * AIAssistantPanel Component
 *
 * Side panel that slides in from the right for AI chat interactions.
 * Supports horizontal resizing by dragging the left edge.
 */

import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState, useCallback } from 'react';

import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';

import type { UseAIChatReturn } from '../../hooks/useAIChat';
import type { UseInvestmentContextReturn } from '../../hooks/useInvestmentContext';

interface AIAssistantPanelProps {
  chat: UseAIChatReturn;
  investmentContext: UseInvestmentContextReturn;
}

const DEFAULT_WIDTH = 414;
const MIN_WIDTH = 320;
const MAX_WIDTH = 800;

/**
 * AIAssistantPanel component
 *
 * Provides a docked side panel for AI chat with:
 * - Header with title and close/clear buttons
 * - Context indicator when viewing an investment
 * - Scrollable message area with auto-scroll
 * - Input area at the bottom
 * - Resizable width by dragging left edge
 */
export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({ chat, investmentContext }) => {
  const { messages, isLoading, error, isOpen, closePanel, sendMessage, clearChat, clearError } =
    chat;

  const { hasContext, contextString, investment, contextType } = investmentContext;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Resizing state
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(DEFAULT_WIDTH);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  // Handle resize mouse events
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = width;
    },
    [width]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) {
        return;
      }

      // Calculate new width (dragging left increases width)
      const delta = resizeStartX.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartWidth.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while resizing
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // Handle send with context
  const handleSend = (text: string) => {
    sendMessage(text, contextString ?? undefined);
  };

  return (
    <>
      {/* Backdrop - subtle overlay that allows main content to remain visible */}
      <div
        className={`fixed inset-0 bg-black/5 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closePanel}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          width: isOpen ? width : DEFAULT_WIDTH,
        }}
        className={`fixed top-0 right-0 h-full z-50 max-w-full bg-white dark:bg-transparent border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col ease-out ${
          isResizing ? '' : 'transition-transform duration-300'
        } ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-label="AI Assistant"
        aria-hidden={!isOpen}
      >
        {/* Dark mode gradient background */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: 'linear-gradient(135deg, #0d1f3c 0%, #0a3d2e 100%)',
          }}
        />
        {/* Resize handle */}
        <div
          onMouseDown={handleResizeStart}
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-primary-500/30 active:bg-primary-500/50 transition-colors z-10"
          title="Drag to resize"
        />

        {/* Header */}
        <div className="relative flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-transparent">
          <div className="flex items-center gap-2">
            {/* Light mode image */}
            <img
              src="/assets/medsuite-ai-sillouette.png"
              alt=""
              className="w-8 h-8 object-contain dark:hidden"
              aria-hidden="true"
            />
            {/* Dark mode image */}
            <img
              src="/assets/medsuite-ai-sillouette-darkmode.png"
              alt=""
              className="w-8 h-8 object-contain hidden dark:block"
              aria-hidden="true"
            />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              MedSuite AI Assistant
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={clearChat}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="New conversation"
              aria-label="Start new conversation"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={closePanel}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close panel"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Context indicator */}
        {hasContext && (
          <div className="relative px-4 py-2 bg-primary-50 dark:bg-white/5 border-b border-primary-100 dark:border-white/10">
            <p className="text-xs text-primary-700 dark:text-primary-300">
              <span className="font-medium">Context:</span>{' '}
              {contextType === 'single' && investment
                ? investment.title
                : contextType === 'list'
                  ? 'All Investments Portfolio'
                  : 'None'}
            </p>
          </div>
        )}

        {/* Messages area */}
        <div className="relative flex-1 overflow-y-auto p-4 space-y-4 scrollbar-auto-hide">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 dark:text-gray-400 mt-8 px-4">
              {/* Light mode image */}
              <img
                src="/assets/medsuite-ai-sillouette.png"
                alt=""
                className="w-[60%] h-auto mx-auto mb-6 opacity-40 dark:hidden"
              />
              {/* Dark mode image */}
              <img
                src="/assets/medsuite-ai-sillouette-darkmode.png"
                alt=""
                className="w-[60%] h-auto mx-auto mb-6 opacity-60 hidden dark:block"
              />
              <p className="font-medium mb-2">Ask me anything about healthcare investments</p>
              <p className="text-sm">
                {hasContext
                  ? "I have context about the investment you're viewing and can help you analyze it."
                  : 'I can help you analyze RCM, EMR, and CM platform investments.'}
              </p>
            </div>
          )}

          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                type="button"
                onClick={clearError}
                className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 mt-1 underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <ChatInput
          onSend={handleSend}
          isLoading={isLoading}
          placeholder={
            hasContext
              ? `Ask about ${investment?.title || 'this investment'}...`
              : 'Ask about investments...'
          }
        />
      </div>
    </>
  );
};

export default AIAssistantPanel;
